

import { Box, Button, Text, useToast } from "@chakra-ui/react";
import dimensionService from "api/dimensions/dimension.service";
import dimensionItemService from "api/dimensions/dimensionitem.service";
import { useQuery, useQueryClient } from "react-query";
import { H2 } from "components/text/headings";
import PropInput from "components/PropInput";
import LifecycleState from "api/common/LifecycleState";
import { useState, useEffect } from "react";
import { useRerender } from "rerenderer";
import { showSuccessToast } from "utils";

/**
 * FIXME - status:
 *  - Add Dimension Item does not save -- the save function misses that there's anything to save.
 *  - Dimenion Items feature needed: see and set the parent dimension item(s).
 * 
 * @param {*} param0 
 * @returns 
 */
export default function DimensionsEditorPanel({ org }) {
	const [mode, setMode] = useState("view");

	const queryClient = useQueryClient();

	const { data: dimensionsPage } = useQuery({
		queryKey: ["dimensions"],
		queryFn: () => dimensionService.list(),
	});
	const dimensions = dimensionsPage?.content ?? [];

	const { data: dimensionItemsPage } = useQuery({
		queryKey: ["dimensionItems"],
		queryFn: () => dimensionItemService.list(),
	});
	const dimensionItems = dimensionItemsPage?.content ?? [];

	const [snapshotDimensions, setSnapshotDimensions] = useState([]);
	const [snapshotDimensionItems, setSnapshotDimensionItems] = useState([]);

	useEffect(() => {
		console.log("SET SNAPSHOT");
		setSnapshotDimensions([...(dimensionsPage?.content ?? [])]);
		setSnapshotDimensionItems([...(dimensionItemsPage?.content ?? [])]);
	}, [dimensionsPage, dimensionItemsPage]);

	// sort the dimensions into a tree structure
	const roots = dimensions.filter(dimension => dimension.parentId === null);
	const toast = useToast();
	const doSave = async () => {
		// check each dimension vs the snapshot dimensions
		for (let i=0; i<dimensions.length; i++) {
			const dimension = dimensions[i];
			const snapshotDimension = snapshotDimensions.find(d => d.id === dimension.id);
			if (snapshotDimension) {
				if (JSON.stringify(dimension) === JSON.stringify(snapshotDimension)) {
					continue;
				}
			}
			// don't save no-name stubs, and dont allow name-less dimensions
			if ( ! dimension.name || ! dimension.id) {
				continue;
			}
			let updatedDimension; // update to get `id` from the server
			if (snapshotDimension) {
				updatedDimension = await dimensionService.update(dimension.id, dimension);
			} else {
				updatedDimension = await dimensionService.create(dimension);
			}
			dimensions[i] = updatedDimension;
		}
		queryClient.invalidateQueries(["dimensions"]);
		for (let i=0; i<dimensionItems.length; i++) {
			const dimensionItem = dimensionItems[i];
			const snapshotDimensionItem = snapshotDimensionItems.find(d => d.id === dimensionItem.id);
			if (snapshotDimensionItem) {
				if (JSON.stringify(dimensionItem) === JSON.stringify(snapshotDimensionItem)) {
					continue;
				}
			}
			// don't save no-name stubs, and dont allow name-less dimension items
			if ( ! dimensionItem.name || ! dimensionItem.id) {
				continue;
			}
			let updatedDimensionItem;
			if (snapshotDimensionItem) {
				updatedDimensionItem = await dimensionItemService.update(dimensionItem.id, dimensionItem);
			} else {
				updatedDimensionItem = await dimensionItemService.create(dimensionItem);
			}
			dimensionItems[i] = updatedDimensionItem;
		}
		queryClient.invalidateQueries(["dimensionItems"]);
		showSuccessToast(toast, "Dimensions and dimension items saved");
	};

	// copy paste of addSubDimension
	const { rerender } = useRerender();
	const addNewDimension = () => {
		const newDimension = {
			name: "",
			state: LifecycleState.ACTIVE
		};
		dimensions.push(newDimension);
		rerender();
	};

	return <div><H2>Dimensions</H2>
		{roots.map(root => <DimensionEditor key={root.id}
			mode={mode}
			dimension={root}
			ancestors={[]}
			allDimensions={dimensions}
			allDimensionItems={dimensionItems} />)}
		{ }
		{mode === "view" &&
			<div><Button variant="secondary" size="sm" onClick={() => setMode("edit")}>Edit Dimensions</Button></div>}
		{mode === "edit" &&
			<><div>
				<Button variant="secondary" size="sm" onClick={addNewDimension}>+ Add Root Dimension</Button>
			</div>
				<div>
					<Button mt={4} variant="primary" onClick={doSave}>Save Dimensions and Dimension Items</Button>
				</div></>
		}
	</div>;
}

function DimensionEditor({ dimension, ancestors, allDimensions, allDimensionItems, mode }) {
	const children = allDimensions.filter(d => d.parentId === dimension.id);
	// const fullId = ancestors.concat(dimension).map(a => a.id).join("->");
	const dimensionItems = allDimensionItems?.filter(item => item.dimensionId === dimension.id);
	const { rerender } = useRerender();
	// console.log("dimension", dimension, "allDimensionItems", allDimensionItems);
	const addValue = () => {
		const newItem = {
			dimensionId: dimension.id,
			name: "",
			state: LifecycleState.ACTIVE
		};
		allDimensionItems.push(newItem);
		rerender();
	};
	const addSubDimension = () => {
		const newDimension = {
			parentId: dimension.id,
			name: "",
			state: LifecycleState.ACTIVE
		};
		allDimensions.push(newDimension);
		rerender();
	};
	const isActive = dimension.state === LifecycleState.ACTIVE || !dimension.state;
	if (mode === "view") {
		return <div style={{ marginBottom: "0.2rem", opacity: isActive ? 1 : 0.5 }}>
			<Box display="flex" flexDirection="row" gap={2}>
				<Text style={{ width: "25%", paddingLeft: ancestors.length * 20 + "px" }}>{dimension.name}</Text>
				<Text>Values: {dimensionItems?.map(item => item.name).join(", ")}</Text>
			</Box>
			{children.map(child => <DimensionEditor key={child.id} dimension={child} ancestors={[...ancestors, dimension]} allDimensions={allDimensions} allDimensionItems={allDimensionItems} mode={mode} />)}
		</div>;
	}
	// editor
	return <div style={{ marginLeft: ancestors.length * 20 + "px", marginBottom: "1rem", opacity: isActive ? 1 : 0.5 }}>
		<Box display="flex" flexDirection="row" gap={2}>
			<PropInput flexGrow={1} label={dimension.id + ": Dimension Name"} prop="name" item={dimension} />
			<PropInput flexGrow={0} prop="state" item={dimension} type="select" 
			options={Object.keys(LifecycleState)} onChange={rerender} />
		</Box>
		<Text>Values</Text>
		{dimensionItems?.map(item => <DimensionItemEditor key={item.id || JSON.stringify(item)} item={item} />)}
		<Button variant="secondary" size="sm" onClick={addValue}>+ Add Value for {dimension.name}</Button>
		{children.length > 0 && <Text>Sub-Dimensions</Text>}
		{children.map(child => <DimensionEditor key={child.id || JSON.stringify(child)} dimension={child} ancestors={[...ancestors, dimension]} allDimensions={allDimensions} allDimensionItems={allDimensionItems} mode={mode} />)}
		<div><Button variant="secondary" size="sm" onClick={addSubDimension}>+ Add Sub-Dimension to {dimension.name}</Button></div>
	</div>;
}

function DimensionItemEditor({ item }) {
	return <Box display="flex" flexDirection="row" gap={2}>
		<Text>{item.id}</Text>
		<PropInput size="sm" flexGrow={1} prop="name" label="Value Name" item={item} />
		<Text>{item.countryIdBelongsTo}</Text>
		<PropInput size="sm" flexGrow={0} prop="state" item={item} type="select" options={Object.keys(LifecycleState)} />
	</Box>;
}